import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { notify } from '@/lib/notifications/engine';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Download, Archive, RefreshCw, UserPlus, MoreHorizontal, Check, Upload, X, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { useAppDialog } from '@/hooks/useAppDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const PARTICIPANT_STATUSES = ['DRAFT', 'ACTIVE', 'INACTIVE', 'ON_HOLD', 'DISCHARGED'];

export default function ParticipantListPage() {
  const { dangerConfirm } = useAppDialog();
  const { user } = useAuth();
  const canDelete = ['Super Admin', 'Admin'].includes(user?.role_name);
  const [participants, setParticipants] = useState([]);
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState('');
  const [fundingFilter, setFundingFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ 
    ndis_number:'', first_name:'', middle_name:'', last_name:'', preferred_name:'',
    date_of_birth:'', gender:'', pronouns:'', 
    phone:'', alternate_phone:'', email:'', 
    address:'', state_id:'', branch_id:'', funding_type:''
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Bulk operations state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatusDialog, setBulkStatusDialog] = useState(false);
  const [bulkArchiveDialog, setBulkArchiveDialog] = useState(false);
  const [bulkAssignDialog, setBulkAssignDialog] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('ACTIVE');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkStaffId, setBulkStaffId] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  const fetchData = async () => {
    try {
      const params = fundingFilter ? `?fundingType=${fundingFilter}` : '';
      const [res, stRes, brRes] = await Promise.all([
        api.get(`/participants${params}`), 
        api.get('/org/states'), 
        api.get('/org/branches')
      ]);
      // API returns 'items' for participants, 'data' for others
      setParticipants(res.data.items || res.data.data || []); 
      setStates(stRes.data.data || []); 
      setBranches(brRes.data.data || []);
      
      // Load staff for bulk assignment - separately with error handling
      try {
        const staffRes = await api.get('/admin/users?page_size=200');
        const allUsers = staffRes.data.data || staffRes.data.items || [];
        setStaff(allUsers.filter(u => u.status === 'ACTIVE' || u.is_active));
      } catch (staffErr) {
        console.warn('Could not load staff for bulk assignment:', staffErr);
      }
    } catch (e) { 
      console.error('Fetch error:', e);
      notify.error(null, 'LOAD_FAILED'); 
    }
  };
  useEffect(() => { fetchData(); }, [fundingFilter]);

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPEG, PNG, WebP, GIF');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum 5MB');
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleDeleteParticipant = async (id, name, e) => {
    e.stopPropagation();
    const ok = await dangerConfirm({
      title: 'Delete participant?',
      description: `"${name}" and all related records will be permanently removed. This action cannot be undone.`,
      confirmLabel: 'Delete participant',
      requireTypeToConfirm: 'DELETE',
      reasonRequired: true,
      testId: 'participant-delete-confirm',
    });
    if (!ok) return;
    try {
      await api.delete(`/participants/${id}`);
      toast.success('Participant deleted');
      fetchData();
    } catch (err) {
      notify.error(err, 'DELETE_FAILED');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const res = await api.post('/participants', form);
      const newId = res.data.data.id;

      // Upload photo if selected
      if (photoFile && newId) {
        try {
          const fd = new FormData();
          fd.append('file', photoFile);
          await api.post(`/participants/${newId}/photo`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch { /* photo upload is optional, don't block creation */ }
      }

      toast.success('Participant created');
      setDialogOpen(false); 
      setForm({ 
        ndis_number:'', first_name:'', middle_name:'', last_name:'', preferred_name:'',
        date_of_birth:'', gender:'', pronouns:'', 
        phone:'', alternate_phone:'', email:'', 
        address:'', state_id:'', branch_id:'', funding_type:''
      });
      setPhotoFile(null);
      setPhotoPreview(null);
      navigate(`/participants/${newId}`);
    } catch (e) { notify.error(e, 'SAVE_FAILED'); }
    finally { setLoading(false); }
  };

  const filtered = participants.filter(p =>
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(search.toLowerCase()) || p.ndis_number?.toLowerCase().includes(search.toLowerCase())
  );

  const handleExportCSV = async () => {
    try {
      toast.info('Preparing export...');
      const response = await api.get('/participants/export/csv', {
        params: { search, status: null, fundingType: fundingFilter || null },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `participants_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (e) {
      notify.error(null, 'EXPORT_FAILED');
    }
  };

  // Bulk selection handlers
  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filtered.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map(p => p.id));
    }
  };

  const clearSelection = () => setSelectedIds([]);

  // Bulk operations handlers
  const handleBulkStatusChange = async () => {
    if (selectedIds.length === 0) return;
    setBulkLoading(true);
    try {
      const res = await api.post('/participants/bulk/status-change', {
        participant_ids: selectedIds,
        new_status: bulkStatus,
        reason: bulkReason || null
      });
      const summary = res.data.summary;
      toast.success(`Status changed: ${summary.success} success, ${summary.failed} failed, ${summary.skipped} skipped`);
      setBulkStatusDialog(false);
      setBulkReason('');
      clearSelection();
      fetchData();
    } catch (e) {
      notify.error(e, 'SAVE_FAILED');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedIds.length === 0) return;
    setBulkLoading(true);
    try {
      const res = await api.post('/participants/bulk/archive', {
        participant_ids: selectedIds,
        reason: bulkReason || null
      });
      const summary = res.data.summary;
      toast.success(`Archived: ${summary.success} success, ${summary.failed} failed, ${summary.skipped} skipped`);
      setBulkArchiveDialog(false);
      setBulkReason('');
      clearSelection();
      fetchData();
    } catch (e) {
      notify.error(e, 'SAVE_FAILED');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkAssignStaff = async () => {
    if (selectedIds.length === 0 || !bulkStaffId) return;
    setBulkLoading(true);
    try {
      const res = await api.post('/participants/bulk/assign-staff', {
        participant_ids: selectedIds,
        staff_id: bulkStaffId,
        role_type: 'SUPPORT_WORKER',
        is_primary: false
      });
      const summary = res.data.summary;
      toast.success(`Staff assigned: ${summary.success} success, ${summary.failed} failed, ${summary.skipped} skipped`);
      setBulkAssignDialog(false);
      setBulkStaffId('');
      clearSelection();
      fetchData();
    } catch (e) {
      notify.error(e, 'SAVE_FAILED');
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Participants</h1>
          <p className="text-muted-foreground mt-1">Manage NDIS participants</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="participant-create-button">
                <Plus className="h-4 w-4 mr-2" /> Add Participant
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Participant</DialogTitle>
                <DialogDescription>Register a new NDIS participant</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>NDIS Number</Label>
                    <Input value={form.ndis_number} onChange={(e) => setForm({...form, ndis_number: e.target.value})} required className="font-mono" data-testid="participant-ndis-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Date of Birth</Label>
                    <Input type="date" value={form.date_of_birth} onChange={(e) => setForm({...form, date_of_birth: e.target.value})} />
                  </div>
                </div>
                {/* FRD Section 6.2 - Name Fields */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} required data-testid="participant-firstname-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Middle Name</Label>
                    <Input value={form.middle_name} onChange={(e) => setForm({...form, middle_name: e.target.value})} placeholder="Optional" />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Preferred Name</Label>
                    <Input value={form.preferred_name} onChange={(e) => setForm({...form, preferred_name: e.target.value})} placeholder="Nickname or preferred name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Pronouns</Label>
                    <Select value={form.pronouns} onValueChange={(v) => setForm({...form, pronouns: v})}>
                      <SelectTrigger><SelectValue placeholder="Select pronouns" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="he/him">He/Him</SelectItem>
                        <SelectItem value="she/her">She/Her</SelectItem>
                        <SelectItem value="they/them">They/Them</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Gender</Label>
                    <Select value={form.gender} onValueChange={(v) => setForm({...form, gender: v})}>
                      <SelectTrigger><SelectValue placeholder="Select gender" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="non_binary">Non-Binary</SelectItem>
                        <SelectItem value="prefer_not_to_say">Prefer Not to Say</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Alternate Phone</Label>
                    <Input value={form.alternate_phone} onChange={(e) => setForm({...form, alternate_phone: e.target.value})} placeholder="Mobile or secondary phone" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Profile Photo</Label>
                  <div className="flex items-center gap-4">
                    {photoPreview ? (
                      <div className="relative">
                        <img src={photoPreview} alt="Preview" className="w-16 h-16 rounded-full object-cover border-2 border-slate-200" />
                        <button
                          type="button"
                          onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                          data-testid="remove-photo-btn"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center">
                        <Upload className="w-5 h-5 text-slate-400" />
                      </div>
                    )}
                    <div>
                      <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-slate-300 bg-white hover:bg-slate-50 transition-colors" data-testid="upload-photo-btn">
                        <Upload className="w-4 h-4" />
                        {photoFile ? 'Change Photo' : 'Upload Photo'}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          className="hidden"
                          onChange={handlePhotoSelect}
                          data-testid="photo-file-input"
                        />
                      </label>
                      <p className="text-xs text-slate-500 mt-1">JPEG, PNG, WebP or GIF. Max 5MB</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Funding Type</Label>
                  <Select value={form.funding_type} onValueChange={(v) => setForm({...form, funding_type: v})}>
                    <SelectTrigger data-testid="participant-funding-type"><SelectValue placeholder="Select funding type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SELF_MANAGED">Self Managed</SelectItem>
                      <SelectItem value="NDIA_MANAGED">NDIA Managed</SelectItem>
                      <SelectItem value="PLAN_MANAGED">Plan Managed</SelectItem>
                      <SelectItem value="SIL_MANAGED">SIL Managed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Select value={form.state_id} onValueChange={(v) => setForm({...form, state_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                      <SelectContent>{states.map(s => <SelectItem key={s.code} value={s.code}>{s.code} - {s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Branch</Label>
                    <Select value={form.branch_id} onValueChange={(v) => setForm({...form, branch_id: v})}>
                      <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                      <SelectContent>{branches.map(b => <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="participant-submit-button">
                  {loading ? 'Creating...' : 'Create Participant'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="participant-search" />
        </div>
        <Select value={fundingFilter} onValueChange={(v) => setFundingFilter(v === 'ALL' ? '' : v)}>
          <SelectTrigger className="w-44" data-testid="funding-type-filter">
            <SelectValue placeholder="All Funding Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Funding Types</SelectItem>
            <SelectItem value="SELF_MANAGED">Self Managed</SelectItem>
            <SelectItem value="NDIA_MANAGED">NDIA Managed</SelectItem>
            <SelectItem value="PLAN_MANAGED">Plan Managed</SelectItem>
            <SelectItem value="SIL_MANAGED">SIL Managed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className="text-sm">
                  {selectedIds.length} selected
                </Badge>
                <Button variant="ghost" size="sm" onClick={clearSelection}>
                  Clear
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkStatusDialog(true)}
                  data-testid="bulk-status-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-1" /> Change Status
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkAssignDialog(true)}
                  data-testid="bulk-assign-btn"
                >
                  <UserPlus className="h-4 w-4 mr-1" /> Assign Staff
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkArchiveDialog(true)}
                  className="text-amber-600 hover:text-amber-700"
                  data-testid="bulk-archive-btn"
                >
                  <Archive className="h-4 w-4 mr-1" /> Archive
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Participants Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={filtered.length > 0 && selectedIds.length === filtered.length}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>NDIS Number</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Funding Type</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Status</TableHead>
                {canDelete && <TableHead className="w-12"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canDelete ? 8 : 7} className="text-center py-8 text-muted-foreground">
                    No participants found
                  </TableCell>
                </TableRow>
              ) : filtered.map(p => (
                <TableRow 
                  key={p.id} 
                  className={`hover:bg-muted/60 cursor-pointer ${selectedIds.includes(p.id) ? 'bg-primary/5' : ''}`}
                  data-testid={`participant-row-${p.id}`}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.includes(p.id)}
                      onCheckedChange={(e) => toggleSelect(p.id, { stopPropagation: () => {} })}
                      onClick={(e) => toggleSelect(p.id, e)}
                      data-testid={`select-${p.id}`}
                    />
                  </TableCell>
                  <TableCell 
                    className="font-medium" 
                    onClick={() => navigate(`/participants/${p.id}`)}
                  >
                    {p.first_name} {p.last_name}
                  </TableCell>
                  <TableCell 
                    className="font-mono text-xs"
                    onClick={() => navigate(`/participants/${p.id}`)}
                  >
                    {p.ndis_number}
                  </TableCell>
                  <TableCell onClick={() => navigate(`/participants/${p.id}`)}>
                    {p.phone || '-'}
                  </TableCell>
                  <TableCell onClick={() => navigate(`/participants/${p.id}`)}>
                    {p.funding_type ? (
                      <span className="text-xs font-medium">{p.funding_type.replace(/_/g, ' ')}</span>
                    ) : (
                      <span className="text-muted-foreground text-xs">-</span>
                    )}
                  </TableCell>
                  <TableCell onClick={() => navigate(`/participants/${p.id}`)}>
                    {p.state_id || '-'}
                  </TableCell>
                  <TableCell onClick={() => navigate(`/participants/${p.id}`)}>
                    <StatusBadge status={p.status || 'active'} />
                  </TableCell>
                  {canDelete && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => handleDeleteParticipant(p.id, `${p.first_name} ${p.last_name}`, e)}
                        className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete participant"
                        data-testid={`delete-participant-${p.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Bulk Status Change Dialog */}
      <Dialog open={bulkStatusDialog} onOpenChange={setBulkStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Status Change</DialogTitle>
            <DialogDescription>
              Change the status for {selectedIds.length} selected participant(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger data-testid="bulk-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PARTICIPANT_STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Input
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                placeholder="Reason for status change..."
                data-testid="bulk-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkStatusDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkStatusChange} 
              disabled={bulkLoading}
              data-testid="confirm-bulk-status"
            >
              {bulkLoading ? 'Processing...' : 'Apply Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Archive Dialog */}
      <Dialog open={bulkArchiveDialog} onOpenChange={setBulkArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Archive</DialogTitle>
            <DialogDescription>
              Archive {selectedIds.length} selected participant(s). This action can be reversed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Reason (optional)</Label>
              <Input
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                placeholder="Reason for archiving..."
                data-testid="bulk-archive-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkArchiveDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive"
              onClick={handleBulkArchive} 
              disabled={bulkLoading}
              data-testid="confirm-bulk-archive"
            >
              {bulkLoading ? 'Processing...' : 'Archive Participants'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Staff Dialog */}
      <Dialog open={bulkAssignDialog} onOpenChange={setBulkAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Assign Staff</DialogTitle>
            <DialogDescription>
              Assign a staff member to {selectedIds.length} selected participant(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Staff Member</Label>
              <Select value={bulkStaffId} onValueChange={setBulkStaffId}>
                <SelectTrigger data-testid="bulk-staff-select">
                  <SelectValue placeholder="Select a staff member..." />
                </SelectTrigger>
                <SelectContent>
                  {staff.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.full_name || `${s.first_name} ${s.last_name}`} ({s.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground">
              The staff member will be added to the participant's support team as a Support Worker.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAssignDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleBulkAssignStaff} 
              disabled={bulkLoading || !bulkStaffId}
              data-testid="confirm-bulk-assign"
            >
              {bulkLoading ? 'Processing...' : 'Assign Staff'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
